import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation, useParams, useNavigate } from "react-router-dom";
import useRetryFetch from "../hooks/useRetryFetch";
import "./Register.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  solid /* regular */,
} from "@fortawesome/fontawesome-svg-core/import.macro"; // <-- import styles to be used
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { config } from "../Environment";

function SystemRoleList({ roles, onChange }) {
  const { t } = useTranslation();
  const listItems = [<option value="">-- {t("select_role_to_add")} --</option>];
  for(let i = 0; i < roles.length; i += 1) {
    listItems.push(<option value={roles[i]} key={i}>{roles[i]}</option>);
  }
  return <select onChange={onChange}>{listItems}</select>
}

function UserRoleList({ roles, removeRole }) {
  const { t } = useTranslation();
  const listItems = [];
  for(let i = 0; i < roles.length; i += 1) {
    listItems.push(<li key={i}>{roles[i]}
    <button onClick={(e) => {e.preventDefault(); removeRole(roles[i]);}}>
    <FontAwesomeIcon
      title={t("delete")}
      icon={solid("times")}
      className="ml-2 mr-2 mt-1 w-[15px]"
    /></button></li>);
  }
  return <ul>{listItems}</ul>
}

function UserProfile() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const retryFetch = useRetryFetch();
  const { id } = useParams();

  const emailRef = useRef();
  const firstNameRef = useRef();
  const lastNameRef = useRef();
  
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [roles, setRoles] = useState([]);
  const [grantRoles, setGrantRoles] = useState([]);
  const [revokeRoles, setRevokeRoles] = useState([]);
  const [roleInput, setRoleInput] = useState("");
  const [createdOn, setCreatedOn] = useState(0);
  const [systemRoles, setSystemRoles] = useState([]);
  const [errMsg, setErrMsg] = useState("");
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    console.log(`UserProfile useEffect called`);
    // load user profile
    (async () => {
      const userProfileResponse = await retryFetch(config.API_BASE + `/api/sysadmin/user/${id}`, { credentials: "include" });
      if (userProfileResponse.ok) {
        const userProfileData = await userProfileResponse.json();
        if (userProfileData.item) {
          const userProfile = userProfileData.item;
          setFirstName(userProfile.firstName ?? '');
          setLastName(userProfile.lastName ?? '');
          setEmail(userProfile.email ?? '');
          setRoles(userProfile.roles ?? []);
          setCreatedOn(userProfile.createdOn ?? 'Unknown');
        }  
      }
    })();
    // load system roles
    (async () => {
      const systemRolesResponse = await retryFetch(config.API_BASE + `/api/sysadmin/roles`, { credentials: "include" });
      if (systemRolesResponse.ok) {
        const systemRolesData = await systemRolesResponse.json();
        if (systemRolesData.list) {
          setSystemRoles(systemRolesData.list);
        }
      }
    })();

    return () => {
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();

    const changeRequest = {};
    if (firstName) {
      changeRequest.firstName = firstName;
    }
    if (lastName) {
      changeRequest.lastName = lastName;
    }
    /*
    if (displayName) {
      changeRequest.displayName = displayName;
    }
      */
    if (email) {
      changeRequest.email = email;
    }
    if (Array.isArray(grantRoles) && grantRoles.length > 0) {
      changeRequest.grantRoles = grantRoles;
    }
    if (Array.isArray(revokeRoles) && revokeRoles.length > 0) {
      changeRequest.revokeRoles = revokeRoles;
    }

    let response = await retryFetch(config.API_BASE + `/api/sysadmin/user/${id}/edit`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(changeRequest),
      credentials: "include",
    });
    if (!response.ok) {
      setErrMsg(t("msg_error_save_failed"));
      //emailRef.current.focus();
    } else {
      setSuccess(true);
      window.setTimeout(() => {
        navigate("/admin");
      }, 2000);
    }
  };

  const changeEmail = (e) => {
    setEmail(e.target.value);
  };
  const changeFirstName = (e) => {
    setFirstName(e.target.value);
  };
  const changeLastName = (e) => {
    setLastName(e.target.value);
  };
  const changeRoleInput = (e) => {
    setRoleInput(e.target.value);
  }

  const addDisplayRole = (role) => {
    if (!roles.includes(role)) {
      const newDisplayRoles = [...roles, role];
      setRoles(newDisplayRoles);
    }
  }

  const addGrantRole = (e) => {
    e.preventDefault();
    if (roleInput && !roles.includes(roleInput)) {
      // if it's a pending revoke role, remove it; otherwise grant the new role
      if (revokeRoles.includes(roleInput)) {
        removeRevokeRole(roleInput);
      } else {
        setGrantRoles([...grantRoles, roleInput]); // this is for submitting to the server
      }
      addDisplayRole(roleInput);
    }
    setRoleInput("");
  };

  const removeDisplayRole = (role) => {
    const newDisplayRoles = [...roles];
    const idx = newDisplayRoles.findIndex((x) => x === role);
    if (idx > -1) {
      newDisplayRoles.splice(idx, 1);
      setRoles(newDisplayRoles);
    }
  };

  const removeGrantRole = (role) => {
    const newGrantRoles = [...grantRoles];
    const idx = newGrantRoles.findIndex((x) => x === role);
    console.log(`removeGrantRole role ${role} idx ${idx} grantroles ${JSON.stringify(grantRoles)}`);
    if (idx > -1) {
      newGrantRoles.splice(idx, 1);
      setGrantRoles(newGrantRoles);  
    }
  };

  const addRevokeRole = (role) => {
    setRevokeRoles([...revokeRoles, role]);
  };

  const removeRevokeRole = (role) => {
    const newRevokeRoles = [...revokeRoles];
    const idx = newRevokeRoles.findIndex((x) => x === role);
    if (idx > -1) {
      newRevokeRoles.splice(idx, 1);
      setRevokeRoles(newRevokeRoles);  
    }
  };

  const removeRole = (role) => {
    // if it's a pending grant role, remove it; otherwise, revoke the existing role
    if (grantRoles.includes(role)) {
      removeGrantRole(role);
    } else {
      addRevokeRole(role);
    }
    removeDisplayRole(role);
  };

  return (
    <div className="h-full w-full flex items-center justify-start py-12">
      <div className="mx-auto grid w-[350px] gap-6">
        <div className="grid gap-2 text-center">
          <h1 className="text-3xl font-bold">{t("user_profile")}</h1>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="grid gap-4">
            <div className="grid gap-2">
              <Label htmlFor="firstName">{t("first_name")}</Label>
              <Input
                id="firstName"
                type="text"
                placeholder=""
                onChange={changeFirstName}
                ref={firstNameRef}
                value={firstName}
                required
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="lastName">{t("last_name")}</Label>
              <Input
                id="lastName"
                type="text"
                placeholder=""
                onChange={changeLastName}
                ref={lastNameRef}
                value={lastName}
                required
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="email">{t("email_address")}</Label>
              <Input
                id="email"
                type="email"
                placeholder="m@example.com"
                onChange={changeEmail}
                ref={emailRef}
                value={email}
                required
              />
            </div>
            <div className="grid gap-2">
              <Label>{t("roles")}</Label>
              <UserRoleList roles={roles} removeRole={removeRole}/>
              <SystemRoleList roles={systemRoles} onChange={changeRoleInput}/>
              <button
                onClick={addGrantRole}
              >
                                      <FontAwesomeIcon
                                        title={t("add")}
                                        icon={solid("plus")}
                                        className="ml-2 mr-2 mt-1 w-[15px]"
                                      />
              {t("add")}
              </button>

                                                  

            </div>
            {errMsg ? <div className="error">{errMsg}</div> : ""}
            {success ? (
              <div className="success">{t("msg_saved")}</div>
            ) : (
              ""
            )}
            <Button type="submit" className="w-full">
              {t("save")}
            </Button>
          </div>
        </form>
        <button
          onClick={() => {
            navigate("/admin?tab=users")
          }}
        >
          {t("cancel")}
        </button>
      </div>
    </div>
  );
}

export default UserProfile;
